import type { ResCollection, ResModel } from '@sysarb/resclient';

const handler = (): void => { };

export class ResgateSubscriptionManager {
    private static instance: ResgateSubscriptionManager;

    private readonly subscriptions: {
        key: string,
        models: { model: ResModel | ResCollection, handler: () => void }[]
    }[] = [];

    private static getInstance(): ResgateSubscriptionManager {
        ResgateSubscriptionManager.instance ??= new ResgateSubscriptionManager();
        return ResgateSubscriptionManager.instance;
    }

    public static remove(storeId: string): void {
        const item = ResgateSubscriptionManager.getInstance().subscriptions.find(c => c.key === storeId);

        if (item == null) return;
        const ridsForKey = item.models.map(c => c.model._rid);

        for (const rid of ridsForKey) {
            const inUseByOther = ResgateSubscriptionManager.getInstance().subscriptions.filter(c => c.key !== storeId).flatMap(c => c.models).map(c => c.model._rid).includes(rid);
            const model = item.models.find(c => c.model._rid === rid);

            if (model != null && !inUseByOther) {
                // no other store is using this model, we unsubscribe it.
                try {
                    item.models.find(c => c.model._rid === rid)?.model.off(null, handler);
                } catch { /* ignore */ }
            }

            if (model != null) {
                item.models.splice(item.models.indexOf(model), 1);
            }
        }
    }

    public static add(storeId: string, model: ResModel | ResCollection): void {
        if (!ResgateSubscriptionManager.getInstance().subscriptions.map(c => c.key).includes(storeId)) {
            ResgateSubscriptionManager.getInstance().subscriptions.push({
                key: storeId,
                models: []
            });
        }

        // all RIDs subscribed
        const rids = ResgateSubscriptionManager.getInstance().subscriptions.flatMap(c => c.models).map(c => c.model._rid);

        if (!rids.includes(model._rid)) {
            // add subscription
            model.on(null, handler);
        }

        // all RIDs subscribed by the current key
        const ridsForKey = ResgateSubscriptionManager.getInstance().subscriptions.filter(c => c.key === storeId).flatMap(c => c.models).map(c => c.model._rid);

        if (!ridsForKey.includes(model._rid)) {
            // add to models subscribed by this key
            ResgateSubscriptionManager.getInstance().subscriptions.find(c => c.key === storeId)?.models.push({
                model,
                handler
            });
        }
    }

    public static getAllSubscriptions(): string[] {
        return ResgateSubscriptionManager.getInstance().subscriptions.flatMap(c => c.models).map(c => c.model._rid);
    }

    public static getAllStoreIds(): string[] {
        return ResgateSubscriptionManager.getInstance().subscriptions.map(c => c.key);
    }

    public static unsubscribeAll(): void {
        for (const store of ResgateSubscriptionManager.getAllStoreIds()) {
            ResgateSubscriptionManager.remove(store);
        }
    }

    public static unsubscribeSpecific(storeId: string, rid: string): void {
        const item = ResgateSubscriptionManager.getInstance().subscriptions.find(c => c.key === storeId);

        if (item == null) {
            return;
        }

        const model = item.models.find(c => c.model._rid === rid);

        if (model != null) {
            model.model.off(null, handler);
            item.models.splice(item.models.indexOf(model), 1);
        }
    }

    public static unsubscribeRid(rid: string): void {
        for (const store of ResgateSubscriptionManager.getAllStoreIds()) {
            ResgateSubscriptionManager.unsubscribeSpecific(store, rid);
        }
    }
}
